import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/common/Toast';
import { restaurantService, type Restaurant } from '@/services/restaurant';
import { productService } from '@/services/product';
import { stockService } from '@/services/stock';
import { checkOrderAvailability } from '@/lib/operatingHours';
import { orderService } from '@/services/order';
import { cartService } from '@/services/cart';
import { useCartStore } from '@/store/cart';
import { getPickupTimestamp } from '@/lib/dateUtils';
import { tossPaymentsService } from '@/services/tossPayments';
import { checkoutService } from '@/services/checkout';
import { DesktopPageContainer } from '@/components/desktop/DesktopPageContainer';

interface CheckoutState {
  storeId: string;
  storeName: string;
  orderType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER';
  items: Array<{
    productId: string;
    productName: string;
    originalPrice: number;
    discountedPrice: number;
    quantity: number;
    currentStock: number;
  }>;
  usedPoints: number;
  totalPrice: number;
  finalPrice: number;
  desiredPickupTime?: string | null;
}

export function DesktopCheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast, ToastContainer } = useToast();
  
  const [checkoutData, setCheckoutData] = useState<CheckoutState | null>(null);
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null);
  const [isValidating, setIsValidating] = useState(true);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [isWidgetReady, setIsWidgetReady] = useState(false);
  const [checkoutToken, setCheckoutToken] = useState<string | null>(null);
  const [orderId, setOrderId] = useState<string | null>(null);
  
  const widgetInitialized = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  // 로그인 상태 확인
  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  // 장바구니에서 전달받은 데이터 확인
  useEffect(() => {
    if (!user) return;
    
    if (!location.state) {
      showToast('결제 정보가 없습니다');
      navigate('/cart');
      return;
    }

    setCheckoutData(location.state as CheckoutState);
  }, [location.state, navigate, showToast, user]);

  // 매장 정보 조회
  useEffect(() => {
    const fetchRestaurant = async () => {
      if (!checkoutData?.storeId) return;

      try {
        const result = await restaurantService.getRestaurantById(checkoutData.storeId, true);
        if (result.success && result.data) {
          setRestaurant(result.data);
        } else {
          setValidationErrors(prev => [...prev, '매장 정보를 불러올 수 없습니다']);
        }
      } catch (error) {
        console.error('Error fetching restaurant:', error);
        setValidationErrors(prev => [...prev, '매장 정보 조회 중 오류가 발생했습니다']);
      }
    };

    fetchRestaurant();
  }, [checkoutData?.storeId]);

  // 실시간 검증 및 체크아웃 토큰 생성
  useEffect(() => {
    const validateAndPrepareCheckout = async () => {
      if (!checkoutData || !restaurant || !user) {
        setIsValidating(false);
        return;
      }

      setIsValidating(true);
      setValidationErrors([]);
      const errors: string[] = [];

      try {
        // 1. 재고 검증
        const stockValidation = await stockService.validateCartStock(
          checkoutData.items.map(item => ({
            productId: item.productId,
            quantity: item.quantity,
            productName: item.productName
          })),
          checkoutData.orderType
        );

        if (!stockValidation.isValid) {
          errors.push(...stockValidation.errors);
        }

        // 2. 주문 가능 시간 검증
        if (!checkOrderAvailability(restaurant, checkoutData.orderType)) {
          switch (checkoutData.orderType) {
            case 'DEADLINE_DISCOUNT':
              errors.push('마감할인 주문 가능 시간이 아닙니다');
              break;
            case 'RESERVATION_DISCOUNT':
              errors.push('예약할인 주문 마감 시간이 지났습니다');
              break;
            case 'REGULAR_ORDER':
              errors.push('현재 주문 가능 시간이 아닙니다');
              break;
          }
        }

        // 2.5. 마감할인의 경우 당일 등록된 오퍼인지 추가 검증
        if (checkoutData.orderType === 'DEADLINE_DISCOUNT') {
          const today = new Date().toISOString().split('T')[0];
          let hasTodayDeadlineOffer = false;
          
          // 장바구니의 각 상품에 대해 당일 등록된 마감할인 오퍼 확인
          for (const item of checkoutData.items) {
            try {
              const productResult = await productService.getProductById(item.productId);
              if (productResult?.offers) {
                const todayOffer = productResult.offers.find((offer: any) => 
                  offer.offer_type === 'DEADLINE_DISCOUNT' && 
                  offer.is_active &&
                  (offer.valid_from ? 
                    new Date(offer.valid_from).toISOString().split('T')[0] === today :
                    true // valid_from이 없으면 당일 등록으로 간주
                  )
                );
                
                if (todayOffer) {
                  hasTodayDeadlineOffer = true;
                  break;
                }
              }
            } catch (error) {
              console.error(`Error checking product offer for ${item.productId}:`, error);
            }
          }
          
          if (!hasTodayDeadlineOffer) {
            errors.push('당일 등록된 마감할인 상품이 아닙니다. 다시 확인해주세요.');
          }
        }

        // 3. 매장 영업 상태 확인
        if (!restaurant.is_open) {
          errors.push('매장이 현재 영업 중이 아닙니다');
        }

        // 4. 예약할인의 경우 추가 검증
        if (checkoutData.orderType === 'RESERVATION_DISCOUNT') {
          const { getTomorrowOperatingHours } = await import('@/lib/operatingHours');
          const tomorrowHours = getTomorrowOperatingHours(restaurant);
          
          if (tomorrowHours.isClosed) {
            errors.push(`내일(${tomorrowHours.dayName})은 휴무일입니다. 예약할인은 이용할 수 없습니다`);
          } else if (!tomorrowHours.openTime || !tomorrowHours.closeTime) {
            errors.push('내일의 영업시간 정보를 확인할 수 없습니다');
          } else if (!checkoutData.desiredPickupTime) {
            errors.push('예약할인은 희망수령시간을 선택해야 합니다');
          }
        }

        if (errors.length === 0) {
          // 5. 체크아웃 검증 및 토큰 생성
          const currentCart = await cartService.getCurrentCart(user.id);
          if (!currentCart) {
            errors.push('장바구니 정보를 찾을 수 없습니다');
          } else {
            // 5-1. 기존 PENDING 주문 확인
            const existingPendingOrder = await orderService.getPendingOrderByCartId(currentCart.id);
            
            if (existingPendingOrder) {
              // 5분 이내면 재사용, 초과면 취소 후 새로 생성
              const elapsed = Date.now() - new Date(existingPendingOrder.created_at).getTime();
              
              if (elapsed < 5 * 60 * 1000) {
                // 5분 이내: 기존 주문 재사용
                console.log('Reusing existing pending order:', existingPendingOrder.id);
                const validationResult = await checkoutService.validateStock(currentCart.id);
                if (validationResult.valid && validationResult.token) {
                  setCheckoutToken(validationResult.token);
                }
                setOrderId(existingPendingOrder.id);
                setValidationErrors(errors);
                setIsValidating(false);
                return; // 기존 주문 재사용, 새로 생성하지 않음
              } else {
                // 5분 초과: 기존 주문 취소
                console.log('Cancelling stale pending order:', existingPendingOrder.id);
                await orderService.cancelPendingOrder(existingPendingOrder.id, '결제 시간 초과');
              }
            }

            // 5-2. 새 주문 생성
            const validationResult = await checkoutService.validateStock(currentCart.id);
            if (validationResult.valid && validationResult.token) {
              setCheckoutToken(validationResult.token);
              
              // 주문 생성 (결제 전)
              const pickupTimestamp = checkoutData.desiredPickupTime 
                ? getPickupTimestamp(checkoutData.orderType, checkoutData.desiredPickupTime)
                : undefined;

              const orderResult = await orderService.createPendingOrderFromCart(user.id, {
                cart_id: currentCart.id,
                pickup_time: pickupTimestamp,
                special_requests: '',
                payment_method: 'CARD'
              });

              if (orderResult.success && orderResult.order) {
                setOrderId(orderResult.order.id);
              } else {
                errors.push(orderResult.error || '주문 생성에 실패했습니다');
              }
            } else {
              errors.push(validationResult.message || '재고 검증에 실패했습니다');
            }
          }
        }

        setValidationErrors(errors);
      } catch (error) {
        console.error('Validation error:', error);
        setValidationErrors(['주문 검증 중 오류가 발생했습니다']);
      } finally {
        setIsValidating(false);
      }
    };

    validateAndPrepareCheckout();
  }, [checkoutData, restaurant, user?.id]);

  // TossPayments 위젯 초기화
  useEffect(() => {
    const initializeWidget = async () => {
      if (!checkoutData || !orderId || validationErrors.length > 0 || widgetInitialized.current) {
        return;
      }

      // 0원(포인트 전액) 결제는 위젯을 렌더링하지 않음
      if (checkoutData.finalPrice <= 0) {
        widgetInitialized.current = true;
        setIsWidgetReady(true);
        return;
      }

      try {
        console.log('🎯 TossPayments 위젯 초기화 시작', {
          orderId,
          finalPrice: checkoutData.finalPrice,
          clientKey: import.meta.env.VITE_TOSSPAYMENTS_CLIENT_KEY?.substring(0, 20) + '...'
        });

        await tossPaymentsService.initialize();
        console.log('✅ TossPayments SDK 초기화 완료');

        // 결제 위젯 렌더링 (setAmount 포함)
          await tossPaymentsService.renderPaymentWidget(
            'payment-widget-container',
            checkoutData.finalPrice,
            {
              variantKey: 'widgetEco'
            }
          );
        console.log('✅ 결제 위젯 렌더링 완료');

        widgetInitialized.current = true;
        setIsWidgetReady(true);
        console.log('✅ 위젯 준비 완료 - 사용자가 결제 가능');
      } catch (error) {
        console.error('❌ Failed to initialize payment widget:', error);
        setValidationErrors(['결제 위젯 초기화에 실패했습니다']);
      }
    };

    initializeWidget();
  }, [checkoutData, orderId, validationErrors]);

  // 페이지 이탈 시 PENDING 주문 정리
  useEffect(() => {
    // cleanup 함수 설정
    cleanupRef.current = () => {
      if (orderId && !isProcessingPayment) {
        console.log('Cleaning up pending order on page unmount:', orderId);
        // 비동기로 취소 처리 (페이지 이탈 시에도 실행됨)
        orderService.cancelPendingOrder(orderId, '결제 페이지 이탈').catch((error: any) => {
          console.error('Failed to cancel pending order on unmount:', error);
        });
      }
    };

    // cleanup 함수 반환
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
      }
    };
  }, [orderId, isProcessingPayment]);

  // 브라우저 뒤로가기/새로고침 감지
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (orderId && !isProcessingPayment) {
        // 주문이 있고 결제 진행 중이 아니면 경고
        e.preventDefault();
        e.returnValue = '결제를 진행 중입니다. 페이지를 떠나시겠습니까?';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [orderId, isProcessingPayment]);

  const handlePayment = async () => {
    // 중복 실행 방지
    if (isProcessingPayment) {
      return;
    }
    
    if (!checkoutData || !user || !orderId || !checkoutToken || validationErrors.length > 0) {
      showToast('결제 조건을 확인해주세요');
      return;
    }
    
    // 위젯 준비 상태 재확인
    if (!isWidgetReady) {
      showToast('결제 위젯 로딩 중입니다. 잠시만 기다려주세요');
      return;
    }

    setIsProcessingPayment(true);

    try {
      // 최종 재고 검증
      const finalStockValidation = await stockService.validateCartStock(
        checkoutData.items.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          productName: item.productName
        })),
        checkoutData.orderType
      );

      if (!finalStockValidation.isValid) {
        showToast('재고가 변경되어 결제할 수 없습니다');
        setValidationErrors(finalStockValidation.errors);
        setTimeout(() => {
          navigate('/cart');
        }, 2000);
        return;
      }

      // 주문명 생성
      const orderName = checkoutData.items.length === 1 
        ? checkoutData.items[0].productName
        : `${checkoutData.items[0].productName} 외 ${checkoutData.items.length - 1}건`;

      // 포인트 전액(0원) 결제는 PG 없이 서버에서 완료 처리
      if (checkoutData.finalPrice <= 0) {
        const result = await tossPaymentsService.completePointPayment(orderId);
        if (!result.success) {
          throw new Error(result.error || '포인트 결제 처리에 실패했습니다.');
        }

        try {
          const currentCart = await cartService.getCurrentCart(user.id);
          if (currentCart) {
            await cartService.clearCart(currentCart.id);
          }
          useCartStore.getState().clearCart(user.id);
        } catch (cleanupError) {
          console.warn('Cart cleanup after point payment failed:', cleanupError);
        }

        showToast('포인트 전액 결제로 주문이 완료되었습니다');
        navigate('/buyer/orders', { replace: true });
        return;
      }

      console.log('💳 TossPayments 결제 요청 시작', {
        orderId,
        orderName,
        amount: checkoutData.finalPrice,
        customerEmail: user.email,
        successUrl: `${window.location.origin}/payment/success`,
        failUrl: `${window.location.origin}/payment/fail`
      });

      // TossPayments 결제 요청
      await tossPaymentsService.requestPayment({
        orderId,
        orderName,
        amount: checkoutData.finalPrice,
        customerEmail: user.email,
        customerName: user.name || user.email?.split('@')[0] || 'Guest',
        customerMobilePhone: user.phone || undefined, // 선택적 필드
        cartId: await cartService.getCurrentCart(user.id).then(cart => cart?.id || ''),
        checkoutToken,
        orderType: checkoutData.orderType,
        restaurantId: checkoutData.storeId,
        userId: user.id
      });

      console.log('✅ TossPayments requestPayment 호출 완료 - 결제창으로 전환됨');

    } catch (error: any) {
      console.error('Payment error:', error);
      
      // 결제 실패 시 장바구니 상태 복원
      try {
        const cartId = await cartService.getCurrentCart(user.id).then(cart => cart?.id || '');
        if (cartId) {
          await tossPaymentsService.handlePaymentFailure(cartId, checkoutData.orderType);
          console.log('Cart state restored after payment failure');
        }
      } catch (restoreError) {
        console.error('Error restoring cart after payment failure:', restoreError);
      }
      
      showToast(error.message || '결제 처리 중 오류가 발생했습니다');
      
      // 2초 후 장바구니 페이지로 이동 (복원된 장바구니를 볼 수 있도록)
      setTimeout(() => {
        navigate('/cart');
      }, 2000);
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const getOrderTypeText = (type: string) => {
    switch (type) {
      case 'DEADLINE_DISCOUNT': return '마감할인';
      case 'RESERVATION_DISCOUNT': return '예약할인';
      case 'REGULAR_ORDER': return '일반주문';
      default: return '';
    }
  };

  if (!checkoutData || !user) return null;

  return (
    <DesktopPageContainer>
      <div className="max-w-6xl mx-auto">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-[32px] font-bold text-gray-900">주문 확인 및 결제</h1>
        </div>

        {/* Main Content - 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Order Information */}
          <div className="space-y-6">
            {/* Store Info */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h2 className="text-[24px] font-bold mb-4">{checkoutData.storeName}</h2>
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 bg-blue-100 text-blue-700 text-[16px] rounded-lg font-medium">
                  {getOrderTypeText(checkoutData.orderType)}
                </span>
              </div>
            </div>

            {/* Order Items */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-[22px] font-bold mb-6">주문 상품</h3>
              <div className="space-y-4">
                {checkoutData.items.map((item, index) => (
                  <div key={index} className="flex justify-between items-start pb-4 border-b last:border-b-0">
                    <div className="flex-1">
                      <p className="text-[18px] font-semibold">{item.productName}</p>
                      <p className="text-[16px] text-gray-600 mt-2">
                        {item.discountedPrice.toLocaleString()}원 × {item.quantity}개
                      </p>
                    </div>
                    <p className="text-[18px] font-bold">
                      {(item.discountedPrice * item.quantity).toLocaleString()}원
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="bg-red-50 p-6 rounded-xl border border-red-200">
                <p className="text-red-700 text-[18px] font-bold mb-4">결제를 진행할 수 없습니다</p>
                <ul className="space-y-2">
                  {validationErrors.map((error, index) => (
                    <li key={index} className="text-[16px] text-red-600">• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Right Column - Payment & Summary */}
          <div className="space-y-6">
            {/* Payment Widget */}
            {!validationErrors.length && orderId && (
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <h3 className="text-[22px] font-bold mb-6">결제 수단 선택</h3>
                
                {/* TossPayments Widget Container */}
                <div id="payment-widget-container" className="min-h-[400px]">
                  {!isWidgetReady && (
                    <div className="flex items-center justify-center h-[400px]">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                        <p className="text-[16px] text-gray-600">결제 위젯 로딩 중...</p>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Agreement Widget */}
                <div id="payment-widget-container-agreement" className="mt-6"></div>
              </div>
            )}

            {/* Price Summary */}
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-[22px] font-bold mb-6">결제 금액</h3>
              <div className="space-y-4">
                <div className="flex justify-between text-[16px]">
                  <span className="text-gray-600">상품 금액</span>
                  <span>{checkoutData.totalPrice.toLocaleString()}원</span>
                </div>
                {checkoutData.usedPoints > 0 && (
                  <div className="flex justify-between text-[16px] text-red-600">
                    <span>포인트 사용</span>
                    <span>-{checkoutData.usedPoints.toLocaleString()}원</span>
                  </div>
                )}
                <div className="flex justify-between text-[20px] font-bold pt-4 border-t">
                  <span>최종 결제 금액</span>
                  <span className="text-green-600">{checkoutData.finalPrice.toLocaleString()}원</span>
                </div>
              </div>
            </div>

            {/* Payment Button */}
            <button
              onClick={handlePayment}
              disabled={isProcessingPayment || isValidating || validationErrors.length > 0 || !isWidgetReady}
              className={`w-full py-6 rounded-xl text-[18px] font-bold transition-colors ${
                isProcessingPayment || isValidating || validationErrors.length > 0 || !isWidgetReady
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-500 text-white hover:bg-green-600'
              }`}
            >
              {isProcessingPayment ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>결제 처리 중...</span>
                </div>
              ) : isValidating ? (
                '검증 중...'
              ) : !isWidgetReady && !validationErrors.length ? (
                '결제 준비 중...'
              ) : (
                `${checkoutData.finalPrice.toLocaleString()}원 결제하기`
              )}
            </button>
          </div>
        </div>
      </div>

      <ToastContainer />
    </DesktopPageContainer>
  );
}
